import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { share } from '@/lib/format';

/* ────────────────────────────────────────────────────────────────────────────
   Bars.

   `FloorRail` is the product's signature graphic: the guaranteed (floor) share
   of a price drawn in iris, the speculative remainder in translucent gold, with
   a hairline marker exactly where the guarantee ends.
   ──────────────────────────────────────────────────────────────────────────── */

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

export interface ProgressProps {
  /** 0..1 */
  value: number;
  tone?: 'bull' | 'floor' | 'up' | 'warn';
  size?: 'sm' | 'md';
  label?: ReactNode;
  showValue?: boolean;
  className?: string;
}

const PROGRESS_FILL = {
  bull: 'bg-bull-sheen',
  floor: 'bg-floor-sheen',
  up: 'bg-up',
  warn: 'bg-warn',
} as const;

export function Progress({
  value,
  tone = 'bull',
  size = 'md',
  label,
  showValue = false,
  className,
}: ProgressProps) {
  const v = clamp01(value);
  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label || showValue ? (
        <div className="flex items-baseline justify-between gap-2">
          {label ? (
            <span className="text-3xs uppercase tracking-[0.14em] text-subtle">{label}</span>
          ) : (
            <span />
          )}
          {showValue ? (
            <span className="font-mono text-2xs tabular-nums text-muted">{share(v)}</span>
          ) : null}
        </div>
      ) : null}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(v * 100)}
        className={cn(
          'relative w-full overflow-hidden rounded-full bg-elevated',
          size === 'sm' ? 'h-1' : 'h-2',
        )}
      >
        <div
          className={cn(
            'h-full origin-left animate-bar-grow rounded-full',
            PROGRESS_FILL[tone],
          )}
          style={{ width: `${v * 100}%` }}
        />
      </div>
    </div>
  );
}

export interface FloorRailProps {
  /** 0..1 — floorPrice / spotPrice. */
  floorRatio: number;
  /** Bar height in px. Reads correctly from 4px up. */
  height?: number;
  showLabels?: boolean;
  spotLabel?: ReactNode;
  floorLabel?: ReactNode;
  className?: string;
}

/**
 * The rail always draws its own "FLOOR" eyebrow, so a caller passing
 * `floorLabel="Floor 6.1e-7"` renders "FLOOR Floor 6.1e-7". Rather than rely on
 * every call site getting that right, drop a leading "floor" from string labels.
 */
function stripFloorWord(label: ReactNode): ReactNode {
  return typeof label === 'string' ? label.replace(/^\s*floor\s+/i, '') : label;
}

/**
 * Two-segment rail. The left segment is the protocol-guaranteed floor, the right
 * is speculative upside. A one-pixel notch marks the boundary so the split is
 * legible even at a 4px bar height.
 */
export function FloorRail({
  floorRatio,
  height = 6,
  showLabels = false,
  spotLabel,
  floorLabel,
  className,
}: FloorRailProps) {
  const ratio = clamp01(floorRatio);
  const width = `${ratio * 100}%`;
  const tall = height >= 8;

  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-1', className)}>
      {showLabels ? (
        <div className="flex items-baseline justify-between gap-2">
          <span className="inline-flex min-w-0 items-baseline gap-1 truncate">
            <span className="text-3xs uppercase tracking-[0.14em] text-floor">Floor</span>
            {floorLabel ? (
              <span className="truncate font-mono text-2xs tabular-nums text-fg">
                {stripFloorWord(floorLabel)}
              </span>
            ) : null}
          </span>
          {spotLabel ? (
            <span className="truncate font-mono text-2xs tabular-nums text-muted">{spotLabel}</span>
          ) : null}
        </div>
      ) : null}

      <div
        role="progressbar"
        aria-label="Share of price guaranteed by the protocol floor"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(ratio * 100)}
        style={{ height }}
        className="relative w-full overflow-hidden rounded-full bg-bull/25"
      >
        {/* guaranteed portion */}
        <div
          className={cn(
            'absolute inset-y-0 left-0 origin-left animate-bar-grow bg-floor-sheen',
            tall ? 'rounded-full' : 'rounded-l-full',
          )}
          style={{ width }}
        />
        {/* boundary notch — the "this is where the guarantee ends" tick */}
        {ratio > 0.02 && ratio < 0.98 ? (
          <div
            aria-hidden="true"
            className="absolute inset-y-0 w-px -translate-x-px bg-canvas/80"
            style={{ left: width }}
          />
        ) : null}
      </div>
    </div>
  );
}

export interface SafetyMeterProps {
  /** 0..100 composite safety score. */
  score: number;
  className?: string;
}

const SEGMENTS = 5;

/** Score → band. Below 40 reads as risk, 80+ as the protocol working as sold. */
function band(score: number) {
  if (score >= 80) return { fill: 'bg-up', text: 'text-up', label: 'Strong' };
  if (score >= 60) return { fill: 'bg-bull', text: 'text-bull', label: 'Solid' };
  if (score >= 40) return { fill: 'bg-warn', text: 'text-warn', label: 'Caution' };
  return { fill: 'bg-down', text: 'text-down', label: 'Fragile' };
}

/** Five stepped bars plus the numeric score — colour is never the only signal. */
export function SafetyMeter({ score, className }: SafetyMeterProps) {
  const value = Number.isFinite(score) ? Math.min(100, Math.max(0, score)) : 0;
  const filled = Math.max(0, Math.min(SEGMENTS, Math.ceil((value / 100) * SEGMENTS)));
  const tone = band(value);

  return (
    <div
      className={cn('flex min-w-0 items-center gap-2', className)}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value)}
      aria-label={`Safety score ${Math.round(value)} of 100 — ${tone.label}`}
    >
      <span aria-hidden="true" className="flex shrink-0 items-end gap-[3px]">
        {Array.from({ length: SEGMENTS }, (_, i) => (
          <span
            key={i}
            className={cn(
              'w-1.5 rounded-sm transition-colors duration-150 ease-swift',
              i < filled ? tone.fill : 'bg-line-strong',
            )}
            style={{ height: 5 + i * 2.5 }}
          />
        ))}
      </span>
      <span className={cn('font-mono text-2xs font-semibold tabular-nums', tone.text)}>
        {Math.round(value)}
      </span>
      <span className="truncate text-3xs uppercase tracking-[0.14em] text-subtle">
        {tone.label}
      </span>
    </div>
  );
}
